<template>
  <div
    id="user"
    v-if="user"
    class="mt-3">
    <!-- User Form -->
    <form
      class="container"
      @submit.prevent="submitUser(user)">

      <!-- First / Last Name -->
      <div class="row">
        <!-- First Name -->
        <div class="col-sm">
          <label for="user-first-name">
            First Name:
            <small class="text-danger">required</small>
          </label>
          <b-form-input
            id="user-first-name"
            ref="userFirstName"
            name="userFirstName"
            type="text"
            placeholder="Enter a first name"
            v-model="user.first_name"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / First Name -->

        <!-- Last Name -->
        <div class="col-sm mt-3 mt-sm-0">
          <label for="user-last-name">
            Last Name:
            <small class="text-danger">required</small>
          </label>
          <b-form-input
            id="user-last-name"
            ref="userLastName"
            name="userLastName"
            type="text"
            placeholder="Enter a last name"
            v-model="user.last_name"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / Last Name -->
      </div>
      <!-- / First / Last Name -->

      <!-- Email / Password -->
      <div class="row mt-3">
        <!-- Email  -->
        <div class="col-sm">
          <label for="user-email">
            Email:
            <small class="text-danger">Required. User will use email to sign in.</small>
          </label>
          <b-form-input
            id="user-email"
            ref="userEmail"
            name="userEmail"
            type="email"
            placeholder="Enter an email"
            v-model="user.email"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / Email  -->

        <!-- Password -->
        <div class="col-sm mt-3 mt-sm-0">
          <label for="user-password">
            Password:
            <small
              class="text-danger"
              v-if="user.id">
              Leave blank if no new password desired.
            </small>
            <small v-if="!user.id" class="text-danger">required</small>
          </label>
          <b-form-input
            id="user-password"
            ref="userPassword"
            name="userPassword"
            type="text"
            placeholder="Enter a password"
            v-model="user.password"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- Password -->
      </div>
      <!-- / Email / Password -->

      <!-- Organization / Phone -->
      <div class="row mt-3">
        <!-- Organization -->
        <div v-if="!user.id" class="col-sm">
          <label for="user-organization">
            Organization:
            <small class="text-danger">Required. Cannot be updated once you save.</small>
          </label>
          <b-form-select
            id="user-organization"
            ref="userOrganization"
            name="userOrganization"
            v-model="user.admin_organization_id"
            :disabled="submitting">
            <option selected :value="undefined">Select Organization</option>
            <option
              v-for="organization in organizations"
              :key="organization.id"
              :value="organization.id">
              {{organization.name}}
            </option>
          </b-form-select>
        </div>
        <!-- / Organization -->

        <!-- Phone -->
        <div
          class="col-sm-6"
          :class="{'mt-3 mt-sm-0': !user.id}">
          <label for="user-phone">
            Phone:
          </label>
          <b-form-input
            id="user-phone"
            ref="userPhone"
            name="userPhone"
            type="tel"
            placeholder="Enter a phone number"
            v-model="user.primary_phone"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / Phone -->
      </div>
      <!-- / Organization / Phone -->

      <div class="corner-buttons">
        <!-- Save User Button -->
        <b-button
          class="shadow"
          type="submit"
          variant="success"
          :disabled="submitting">
          <font-awesome-icon icon="save"></font-awesome-icon> Save
        </b-button>
        <!-- / Save User Button -->

        <!-- Delete User Button -->
        <b-button
          class="shadow ml-3"
          type="button"
          variant="danger"
          v-if="user.id"
          :disabled="submitting"
          @click="submitUser(user.id)">
          <font-awesome-icon icon="trash"></font-awesome-icon> Delete User
        </b-button>
        <!-- / Delete User Button -->
      </div>
    </form>
    <!-- / User Form -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import DirtyForm from '../services/dirty-form';
import AppMixin from '../services/app-mixin';

export default {
  name: 'user',
  mixins: [
    DirtyForm,
    AppMixin,
  ],
  data() {
    return {
      user: false,
      userCopy: false,
      submitting: false,
    };
  },
  computed: {
    userDirty() {
      const userJSON = JSON.stringify(this.user);
      const userCopyJSON = JSON.stringify(this.userCopy);
      return userJSON !== userCopyJSON;
    },
    anyDirty() {
      return this.userDirty;
    },
  },
  methods: {
    submitUser(user) {
      let userPromise;

      this.submitting = true;

      // validate organization selected if creating a new organizer
      if (!this.user.admin_organization_id && !this.user.id) {
        this.submitting = false;
        return this.showWarning(
          '&bull; Organization is required.',
          'The follow errors occurred',
        );
      }

      if (typeof user !== 'object') {
        userPromise = this.deleteUser(user);
      } else if (user.id) {
        userPromise = this.updateUser(user);
      } else {
        userPromise = this.createUser(user);
      }

      userPromise
        .then(() => {
          this.$router.push({ name: 'organizers' });
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });

      return userPromise;
    },
    createUser(user) {
      this.showToast('Creating user...');

      user.username = user.email;

      return DataSvc.createUser(user)
        .then(u => this.delay(1000, u))
        .then((u) => {
          this.user = u;
          this.userCopy = _.cloneDeep(u);
          return DataSvc.updatePermissions(u.id, ['login', 'organizers']);
        })
        .then(() => {
          this.showToast('User created!');
        });
    },
    updateUser(user) {
      this.showToast('Updating user...');

      user.username = user.email;

      return DataSvc.updateUser(user)
        .then((u) => {
          this.user = u;
          this.userCopy = _.cloneDeep(u);
          return this.delay(1000);
        })
        .then(() => {
          this.showToast('User updated!');
        });
    },
    deleteUser(userID) {
      const confirmDelete = () => {
        this.showToast('Deleting user...');

        return DataSvc.deleteUser(userID)
          .then(() => {
            this.showToast('User deleted!');
          });
      };

      return this.showConfirm(
        'Are you sure you want to delete this user?',
        'Deleting a user',
      ).then(confirmDelete);
    },
  },
  created() {
    this.showGlobalLoader('Loading user...');

    const initUser = () => {
      const { userID } = this.$route.params;
      let userPromise;

      if (userID === 'new') {
        userPromise = Promise.resolve({});
      } else {
        userPromise = DataSvc.getUser(userID);
      }

      return userPromise
        .catch(() => {
          this.showWarning('That user does not exist.');
          this.$router.replace({ name: 'organizers' });
        })
        .then((user) => {
          this.user = _.pick(
            user,
            [
              'id',
              'first_name',
              'last_name',
              'email',
              'primary_phone',
            ],
          );

          this.userCopy = _.cloneDeep(this.user);

          // focus first input
          this.$nextTick(() => {
            this.focusInput('userFirstName');
          });
        });
    };

    this.initPromise.then(initUser).finally(this.hideGlobalLoader);
  },
};
</script>
